import React, { useState, useMemo, useRef, useEffect } from "react";
import { Table, Spinner } from "reactstrap";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import "./DataGrid.css";

// Import your local SVG assets
import { ReactComponent as Sort } from "assets/img/svg/sort.svg";
import { ReactComponent as SortDesc } from "assets/img/svg/sortDesc.svg";
import { failureToast } from "../utils/methods/toasterFunctions/function";

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;
    useEffect(() => {
      if (resolvedRef.current)
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);
    return <input type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

const ServerDataGrid = ({
  columns: userColumns,
  rows = [],
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  // We don't need onSortChange from parent anymore for client-side sorting
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  rowKey = "id",
  height = "400px",
  pageSizeOptions = [10, 20, 50, 100, 200, 300],
  hidePagination = false,
  isSingleSelect = false,
  isShowPageTracker = true,
  rowSelectable,//for row level selection control, pass a function that receives the row and returns true/false
}) => {
  // --- 1. INTERNAL SORT STATE ---
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // --- 2. CLIENT-SIDE SORTING LOGIC ---
  const sortedRows = useMemo(() => {
    // If no sort key is selected, return the raw rows
    if (!sortConfig.key) return rows;

    // Create a shallow copy to sort so we don't mutate props
    const sorted = [...rows];

    sorted.sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      // Handle nulls/undefined (push to bottom)
      if (valA == null) return 1;
      if (valB == null) return -1;

      // Numeric Sort
      if (typeof valA === "number" && typeof valB === "number") {
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }

      // String Sort (Smart comparison for "Item 2" vs "Item 10")
      const strA = String(valA);
      const strB = String(valB);

      const comparison = strA.localeCompare(strB, undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [rows, sortConfig]);

  // --- 3. HANDLERS ---

  // Handle Header Click to update Sort State
  const handleHeaderClick = (key) => {
    let direction = "asc";
    // Toggle logic: If already sorting by this key in ASC, switch to DESC
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectRow = (id, row) => {
    if (rowSelectable && !rowSelectable(row)) return;
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) newSelectedIds.delete(id);
    else {
      if (isSingleSelect && newSelectedIds.size > 0) {
        failureToast("Only single selection allowed");
        return;
      }
      newSelectedIds.add(id);
    }
    onSelectionChange && onSelectionChange(newSelectedIds);
  };

  const handleSelectAll = (e) => {
    // Select the currently visible/sorted rows
    if (isSingleSelect) {
      failureToast("Only single selection allowed");
      return ;
    }; // No "select all" in single select mode
    const selectableRows = rowSelectable
    ? sortedRows.filter((row) => rowSelectable(row))
    : sortedRows;
    const currentIds = selectableRows.map((row) => row[rowKey]);
    const newSelectedIds = new Set(selectedIds);
    if (e.target.checked) currentIds.forEach((id) => newSelectedIds.add(id));
    else currentIds.forEach((id) => newSelectedIds.delete(id));
    onSelectionChange && onSelectionChange(newSelectedIds);
  };

  // Render Helpers
  const selectableRows = rowSelectable
  ? sortedRows.filter((row) => rowSelectable(row))
  : sortedRows;

const currentIds = selectableRows.map((row) => row[rowKey]);
  const isAllSelected =
    currentIds.length > 0 && currentIds.every((id) => selectedIds.has(id));
  const isIndeterminate =
    currentIds.some((id) => selectedIds.has(id)) && !isAllSelected;

  // --- Column Configuration ---
  const tableColumns = useMemo(() => {
    if (!selectable) return userColumns;
    const checkboxColumn = {
      key: "__selection__",
      label: "",
      pinned: true,
      width: 40,
      align: "center",
      className: "checkbox-cell",
      render: (_, row) => {
        const disabled = rowSelectable ? !rowSelectable(row) : false;
       return( <input
          type="checkbox"
          checked={selectedIds.has(row[rowKey])}
          onChange={() => handleSelectRow(row[rowKey], row)}
          disabled={disabled}
        />
       );
      },
    };
    return [checkboxColumn, ...userColumns];
  }, [selectable, userColumns, selectedIds, rowKey]);

  // --- Sticky Styles Logic ---
  const getStickyStyles = (col, index) => {
    if (!col.pinned) return {};
    const styles = {};
    if (index === 0 && col.pinned) styles.left = 0;
    else {
      let rightOffset = 0;
      for (let i = index + 1; i < tableColumns.length; i++) {
        if (tableColumns[i].pinned) rightOffset += tableColumns[i].width || 100;
      }
      styles.right = `${rightOffset}px`;
    }
    styles.width = col.width ? `${col.width}px` : "auto";
    return styles;
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    if (!pagination) return pages;
    for (let i = 1; i <= pagination?.totalPages; i++) pages.push(i);
    return pages;
  }, [pagination]);

  const startItem =
    pagination?.total === 0
      ? 0
      : (pagination?.page - 1) * pagination?.pageSize + 1;

  const endItem = Math.min(
    pagination?.page * pagination?.pageSize,
    pagination?.total
  );

  return (
    <div className="datagrid-wrapper" style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner color="primary">Loading...</Spinner>
        </div>
      )}

      <div className="table-responsive-custom" style={{ height: height }}>
        <Table hover className="custom-table mb-0">
          <thead>
            <tr>
              {tableColumns.map((col, idx) => {
                const isSorted =
                  sortConfig?.key === col.key && col.key !== "__selection__";
                const stickyStyle = getStickyStyles(col, idx);
                const stickyClass = col.pinned ? "sticky-col" : "";
                const isBoundary =
                  col.pinned && idx > 0 && !tableColumns[idx - 1].pinned;
                const dividerClass = isBoundary ? "sticky-col-divider" : "";

                return (
                  <th
                    key={col.key}
                    style={{ ...stickyStyle, minWidth: col.width || "120px" }}
                    className={`${stickyClass} ${dividerClass} ${col.className || ""
                      }`}
                    // CLICK HANDLER: Updates local state
                    onClick={() => col.sortable && handleHeaderClick(col.key)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: col.align || "flex-start",
                        height: "100%",
                      }}
                    >
                      {col.key === "__selection__" ? (
                        <IndeterminateCheckbox
                          indeterminate={isIndeterminate}
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                      ) : (
                        <>
                          {col.label}
                          {col.sortable && (
                            <div
                              className={`sort-icon-wrapper ${isSorted ? "sort-active" : ""
                                }`}
                            >
                              {(!isSorted ||
                                sortConfig.direction === "asc") && (
                                  <Sort
                                    style={{ width: "12px", height: "12px" }}
                                  />
                                )}
                              {(!isSorted ||
                                sortConfig.direction === "desc") && (
                                  <SortDesc
                                    style={{ width: "12px", height: "12px" }}
                                  />
                                )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={loading ? "opacity-50" : ""}>
            {sortedRows.length === 0 && !loading ? (
              <tr>
                <td colSpan={tableColumns.length} className="text-center p-4">
                  No data found
                </td>
              </tr>
            ) : (
              // RENDER LOOP: Iterate over sortedRows, not rows
              sortedRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {tableColumns.map((col, colIndex) => {
                    const stickyStyle = getStickyStyles(col, colIndex);
                    const stickyClass = col.pinned ? "sticky-col" : "";
                    const isBoundary =
                      col.pinned &&
                      colIndex > 0 &&
                      !tableColumns[colIndex - 1].pinned;
                    const dividerClass = isBoundary ? "sticky-col-divider" : "";

                    return (
                      <td
                        key={`${rowIndex}-${col.key}`}
                        style={stickyStyle}
                        className={`${stickyClass} ${dividerClass} ${col.className || ""
                          }`}
                      >
                        <div style={{ textAlign: col.align || "left" }}>
                          {col.render
                            ? col.render(row[col.key], row)
                            : row[col.key]}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {!hidePagination && (
        <div className="datagrid-footer">
          <div className="datagrid-footer-left-items">
            <div className="d-flex align-items-center">
              <select
                className="footer-select"
                value={pagination.pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                disabled={loading}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>Items per page</span>
            </div>
            <div style={{ color: "#6c757d" }}>
              {startItem} - {endItem} of {pagination.total} items
            </div>

          </div>
          <div className="d-flex align-items-center gap-2">
            {isShowPageTracker && (
              <div className="page-tracker">
                <select
                  className="page-select-dropdown"
                  value={pagination.page}
                  onChange={(e) => onPageChange(Number(e.target.value))}
                  disabled={loading}
              >
                {pageNumbers.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <span style={{ color: "#adb5bd" }}>
                of {pagination.totalPages} pages
              </span>
            </div>)}
            <button
              className="pagination-btn"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
            >
              <AiOutlineLeft size={14} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || loading}
            >
              <AiOutlineRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerDataGrid;
